import React, { useState, useEffect, useCallback } from 'react';
import { 
  getGoogleDriveIntegrationStatus, 
  initiateGoogleDriveOAuth, 
  disconnectGoogleDrive,
  listGoogleDriveFiles,
  listGoogleSheets,
  IntegrationStatus 
} from '../../api/integrations';
import { toast } from 'sonner';
import LoadingComponent from '../LoadingComponent';
import moment from 'moment';

interface GoogleDriveIntegrationProps {
  communityId: string;
  userId: string;
  onClose?: () => void;
}

interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  webViewLink?: string;
}

const GoogleDriveIntegration: React.FC<GoogleDriveIntegrationProps> = ({ 
  communityId, 
  userId, 
  onClose 
}) => {
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [sheets, setSheets] = useState<DriveFile[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [activeTab, setActiveTab] = useState<'files' | 'sheets'>('files');

  const loadStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const integrationStatus = await getGoogleDriveIntegrationStatus(communityId, userId);
      setStatus(integrationStatus);
    } catch (error) {
      console.error('Error loading Google Drive status:', error);
      toast.error('Failed to load Google Drive integration status');
    } finally {
      setIsLoading(false);
    }
  }, [communityId, userId]);

  const loadFiles = useCallback(async () => {
    try {
      setIsLoadingFiles(true);
      if (activeTab === 'files') {
        const filesData = await listGoogleDriveFiles(communityId, userId);
        setFiles(filesData.files || []);
      } else {
        const sheetsData = await listGoogleSheets(communityId, userId);
        setSheets(sheetsData.files || []);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      toast.error('Failed to load files');
    } finally {
      setIsLoadingFiles(false);
    }
  }, [communityId, userId, activeTab]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (status?.connected) {
      loadFiles();
    }
  }, [status?.connected, loadFiles]);

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      const response = await initiateGoogleDriveOAuth(communityId, userId);
      if (response.authUrl) {
        // Open OAuth flow in new window
        const popup = window.open(
          response.authUrl,
          'Google Drive OAuth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        // Listen for OAuth completion
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            setIsConnecting(false);
            loadStatus(); // Refresh status
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error connecting Google Drive:', error);
      toast.error('Failed to initiate Google Drive connection');
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Drive?')) {
      return;
    }

    try {
      await disconnectGoogleDrive(communityId, userId);
      toast.success('Google Drive disconnected successfully');
      await loadStatus();
      setFiles([]);
      setSheets([]);
    } catch (error) {
      console.error('Error disconnecting Google Drive:', error);
      toast.error('Failed to disconnect Google Drive');
    }
  };

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <div className="w-full p-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Google Drive Integration</h2>
          {status?.connected && (
            <button
              onClick={handleDisconnect}
              className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 border border-red-300 rounded-md hover:bg-red-50"
            >
              Disconnect
            </button>
          )}
        </div>

        {status?.connected ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-green-800 font-medium">Connected</span>
              </div>
            </div>

            {status.accountName && (
              <div className="text-sm text-gray-600">
                <p><strong>Account:</strong> {status.accountName}</p>
                {status.lastSyncedAt && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Last Synced</p>
                      <p className="text-sm text-gray-700 font-semibold">{moment(status.lastSyncedAt).fromNow()}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{moment(status.lastSyncedAt).format('MMM D, YYYY [at] h:mm A')}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tabs for Files and Sheets */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('files')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'files'
                      ? 'border-primary-200 text-primary-200'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Files & Folders
                </button>
                <button
                  onClick={() => setActiveTab('sheets')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'sheets'
                      ? 'border-primary-200 text-primary-200'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Google Sheets
                </button>
              </nav>
            </div>

            {/* Files/Sheets List */}
            {isLoadingFiles ? (
              <LoadingComponent />
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {activeTab === 'files' ? (
                  files.length > 0 ? (
                    <ul className="space-y-2">
                      {files.map((file) => (
                        <li key={file.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex items-center">
                            <svg className="w-5 h-5 text-gray-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                            </svg>
                            <span className="text-sm font-medium text-gray-900">{file.name}</span>
                          </div>
                          {file.webViewLink && (
                            <a
                              href={file.webViewLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary-200 hover:text-primary-300"
                            >
                              Open
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No files found</p>
                  )
                ) : (
                  sheets.length > 0 ? (
                    <ul className="space-y-2">
                      {sheets.map((sheet) => (
                        <li key={sheet.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex items-center">
                            <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                            </svg>
                            <span className="text-sm font-medium text-gray-900">{sheet.name}</span>
                          </div>
                          {sheet.webViewLink && (
                            <a
                              href={sheet.webViewLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary-200 hover:text-primary-300"
                            >
                              Open
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No Google Sheets found</p>
                  )
                )}
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Available Features</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Access files and folders from Google Drive</li>
                <li>• View and manage Google Sheets</li>
                <li>• Sync documents directly from your dashboard</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              Connect your Google Drive account to sync files and documents directly from your dashboard.
            </p>
            <button
              onClick={handleConnect}
              disabled={isConnecting}
              className="px-6 py-3 bg-primary-200 hover:bg-primary-300 text-white font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnecting ? 'Connecting...' : 'Connect Google Drive'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleDriveIntegration;

